import { useSyncExternalStore } from "react";

export type DateCalendarMode = "AD" | "BS";

const STORAGE_KEY = "sagemin_date_calendar";

/**
 * Single global AD/BS preference shared by every date field on the page
 * (module-level store, not per-component state) — changing it anywhere
 * updates every mounted field immediately, and it's remembered across
 * reloads/tabs via localStorage.
 */
let mode: DateCalendarMode = "AD";
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

if (typeof window !== "undefined") {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "AD" || stored === "BS") mode = stored;

  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY) return;
    if (event.newValue === "AD" || event.newValue === "BS") {
      mode = event.newValue;
      notify();
    }
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return mode;
}

function getServerSnapshot(): DateCalendarMode {
  return "AD";
}

export function setDateCalendarMode(next: DateCalendarMode) {
  mode = next;
  window.localStorage.setItem(STORAGE_KEY, next);
  notify();
}

export function useDateCalendarMode(): [
  DateCalendarMode,
  (mode: DateCalendarMode) => void,
] {
  const current = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return [current, setDateCalendarMode];
}
